import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Input, Select } from 'antd';
import Table from '@components/Table';
import { useTranslation } from 'react-i18next';
import SessionContext from '@providers/Session/SessionContext';
import useSwr from '@hooks/useSwr';
import useSearchParams from '@hooks/useSearchParams';
import {
  dateFormat,
  formatCurrency,
  getDateInISOFormat,
} from '@utils/formatters';
import SelectCustomers from '@components/SelectCustomers';
import QuickRangePicker from '@components/DatePicker/QuickRangePicker';
import moment from 'moment';
import BackToListing from '@components/BackToListing';
import CustomTag from '@components/CustomTag';

const { Search } = Input;

const InvoiceActionDetails = () => {
  const { hasCustomersRoles } = useContext(SessionContext);
  const [protocolValue, setProtocolValue] = useState('');
  const [descriptionValue, setDescriptionValue] = useState('');
  const [createdByValue, setCreatedByValue] = useState('');
  const [invoiceIdValue, setInvoiceIdValue] = useState('');

  const { t } = useTranslation('invoices-actions');

  const {
    setSearchParams,
    searchParams,
    hasValuesInExtraParams,
    clearSearchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: [
      'description',
      'createdBy',
      'customerId',
      'startDate',
      'endDate',
      'invoiceId',
      'actionType',
      'status',
      'protocol',
    ],
  });

  const {
    description,
    createdBy,
    customerId,
    startDate,
    endDate,
    invoiceId,
    actionType,
    status,
    protocol,
    page,
    linesPerPage,
  } = useMemo(() => searchParams, [searchParams]);

  useEffect(() => {
    setDescriptionValue(description);
  }, [description]);

  useEffect(() => {
    setProtocolValue(protocol);
  }, [protocol]);

  const actionsTypesOptions = useMemo(
    () => [
      { label: t('action-type.ADD_ITEM'), value: 'ADD_ITEM' },
      { label: t('action-type.EDIT_ITEM'), value: 'EDIT_ITEM' },
      { label: t('action-type.REMOVE_ITEM'), value: 'REMOVE_ITEM' },
    ],
    [t],
  );

  const { data } = useSwr(
    `/service-proxy/broker/invoice-actions`,
    {
      params: {
        page: page && Number(page) - 1,
        linesPerPage,
        protocol,
        description,
        createdBy,
        actionType,
        status,
        invoiceId,
        customerId,
        startDate,
        endDate,
      },
    },
    { refreshInterval: 10 * 2000 },
  );

  const statusOptions = useMemo(
    () => [
      { label: t('status.SUCCESS'), value: 'SUCCESS' },
      { label: t('status.QUEUED'), value: 'QUEUED' },
      { label: t('status.CUSTOMER_NOT_FOUND'), value: 'CUSTOMER_NOT_FOUND' },
      { label: t('status.INVOICE_NOT_FOUND'), value: 'INVOICE_NOT_FOUND' },
      { label: t('status.INSERT_ERROR'), value: 'INSERT_ERROR' },
      { label: t('status.UNEXPECTED_ERROR'), value: 'UNEXPECTED_ERROR' },
      { label: t('status.ITEM_NOT_FOUND'), value: 'ITEM_NOT_FOUND' },
      { label: t('status.ITEM_NOT_EDITABLE'), value: 'ITEM_NOT_EDITABLE' },
      { label: t('status.ITEM_NOT_REMOVABLE'), value: 'ITEM_NOT_REMOVABLE' },
      { label: t('status.ERROR_REMOVING_ITEM'), value: 'ERROR_REMOVING_ITEM' },
      {
        label: t('status.ACTION_ALREADY_QUEUED'),
        value: 'ACTION_ALREADY_QUEUED',
      },
    ],
    [t],
  );

  const columns = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
      },
      {
        title: t('table.customer'),
        dataIndex: 'customer',
        render: (customer: any) => customer?.name,
      },
      { title: t('table.protocol'), dataIndex: 'protocol' },
      { title: t('table.invoiceId'), dataIndex: 'invoiceId' },
      {
        title: t('table.actionType'),
        dataIndex: 'actionType',
        render: (value: string) => <span>{t(`action-type.${value}`)}</span>,
      },
      { title: t('table.createdBy'), dataIndex: 'createdBy' },
      {
        title: t('table.itemId'),
        dataIndex: 'itemId',
      },
      {
        title: t('table.description'),
        dataIndex: 'description',
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        render: (value: any) => dateFormat(value),
      },
      {
        title: t('table.processedAt'),
        dataIndex: 'processedAt',
        render: (value: any) => dateFormat(value),
      },
      {
        title: t('table.value'),
        dataIndex: 'value',
        render: (value: any) => formatCurrency(Number(value)),
      },
      {
        title: t('table.quantity'),
        dataIndex: 'quantity',
        align: 'center' as const,
        render: (value: any) => <CustomTag>{value}</CustomTag>,
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        render: (value: string) => {
          let color: 'red' | 'green' | 'blue' = 'red';
          if (value === 'SUCCESS') {
            color = 'green';
          } else if (value === 'QUEUED') {
            color = 'blue';
          }
          return <CustomTag color={color}>{t(`status.${value}`)}</CustomTag>;
        },
      },
    ],
    [t],
  );

  const handleDateChange = useCallback(
    (date: { startDate: string | null; endDate: string | null }) => {
      setSearchParams({
        startDate: getDateInISOFormat(date?.startDate),
        endDate: getDateInISOFormat(date?.endDate),
      });
    },
    [setSearchParams],
  );

  const tableFilters = useMemo(
    () => [
      {
        visible: hasCustomersRoles,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.customer'),
        element: (
          <SelectCustomers
            value={customerId && Number(customerId)}
            onChange={value => setSearchParams({ customerId: value })}
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.protocol'),
        element: (
          <Search
            onSearch={value => setSearchParams({ protocol: value })}
            onChange={({ target: { value } }) => setProtocolValue(value)}
            value={protocolValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.description'),
        element: (
          <Search
            onSearch={value => setSearchParams({ description: value })}
            onChange={({ target: { value } }) => setDescriptionValue(value)}
            value={descriptionValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.invoiceId'),
        element: (
          <Search
            onSearch={value => setSearchParams({ invoiceId: value })}
            onChange={({ target: { value } }) => setInvoiceIdValue(value)}
            value={invoiceIdValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        label: t('table.status'),
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        element: (
          <Select
            optionFilterProp="label"
            options={statusOptions}
            style={{ width: '100%' }}
            value={status}
            allowClear
            showSearch
            onChange={value => setSearchParams({ status: value })}
          />
        ),
      },
      {
        visible: true,
        label: t('table.actionType'),
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        element: (
          <Select
            optionFilterProp="label"
            options={actionsTypesOptions}
            style={{ width: '100%' }}
            value={actionType}
            allowClear
            showSearch
            onChange={value => setSearchParams({ actionType: value })}
          />
        ),
      },
      {
        visible: true,
        label: t('table.createdBy'),
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        element: (
          <Search
            onSearch={value => setSearchParams({ createdBy: value })}
            onChange={({ target: { value } }) => setCreatedByValue(value)}
            value={createdByValue}
            allowClear
          />
        ),
      },
      {
        visible: true,
        col: {
          lg: 10,
          xl: 8,
          xs: 24,
        },
        label: t('table.date'),
        element: (
          <QuickRangePicker
            onChange={handleDateChange}
            defaultValue={
              startDate
                ? [moment(startDate, 'YYYYMMDD'), moment(endDate, 'YYYYMMDD')]
                : undefined
            }
            style={{ width: '100%' }}
          />
        ),
      },
    ],
    [
      hasCustomersRoles,
      t,
      customerId,
      protocolValue,
      descriptionValue,
      invoiceIdValue,
      statusOptions,
      status,
      actionsTypesOptions,
      actionType,
      createdByValue,
      handleDateChange,
      startDate,
      endDate,
      setSearchParams,
    ],
  );

  return (
    <Table
      title={<BackToListing style={{ margin: 0 }} title={t('title-details')} />}
      data={data?.content}
      columns={columns}
      loading={!data}
      onTableChange={setSearchParams}
      tableKey="invoiceActionDetails"
      pagination={{ page, linesPerPage, total: data?.totalElements }}
      filters={{
        list: tableFilters,
        hasValuesInFilters: hasValuesInExtraParams,
        openByDefault: true,
        onClearFilters: () => clearSearchParams(),
      }}
    />
  );
};

export default InvoiceActionDetails;
